/// <reference types="@types/google.maps" />
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { DataService } from 'src/app/services/data.service';
import { STATES } from 'src/app/shared/constants/states';
import { ERROR_MESSAGES } from 'src/app/shared/constants/text-field';
import {
  Observable,
  Subscription,
  catchError,
  debounceTime,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { EventData, FormData, Property } from 'src/app/services/data.model';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';
import { dataParams } from 'src/app/shared/constants/data-params';
import { NavigationService } from 'src/app/services/navigation.service';
import { RadioComponent } from 'src/app/shared/components/radio/radio.component';

declare var google: any;

@Component({
  selector: 'app-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MainLayoutComponent,
    FormGroupComponent,
    NgSelectModule,
    RadioComponent,
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.scss',
})
export class AddressComponent implements OnInit, OnDestroy, AfterViewInit {
  addressForm: FormGroup;
  statesList = STATES;
  ERROR_MESSAGES = ERROR_MESSAGES;
  previouslyFilledData: {
    property: Property;
    sessionData: string;
  };
  autocomplete;
  inputElement: HTMLInputElement;
  interval;
  counter = 0;
  dataParams = null;
  subscription: Subscription = new Subscription();
  nextStep = NAVIGATION_MAP[PAGES.ADDRESS].nextButton.route;
  insideSalesReasonEnum: number;
  nonPrimaryResidenceReasons = [876090001, 876090002, 876090003];
  otherPropertyType = 876090099;
  windowCount: number = 0;
  isEvent = false;
  ownershipIds = {
    LEAD_IS_THE_HOMEOWNER: 'lead_is_the_homeowner',
    IM_NOT_SURE: 'im_not_sure',
  };
  ownershipOptions = [
    {
      id: this.ownershipIds.LEAD_IS_THE_HOMEOWNER,
      value:
        'Lead is the homeowner OR the listed homeowner can also attend the meeting',
    },
    {
      id: this.ownershipIds.IM_NOT_SURE,
      value: 'I’m not sure',
    },
  ];
  ownershipOptionsRadioGroup = [];
  propertyOwners = [];
  ownershipRequiresVerification = false;
  otherAddressProperties = {
    zipcodeGuid: '',
    marketSegmentGuid: '',
    zipcodeValid: true,
  };

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    public nextButtonState: NextButtonStateService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.getData();
    this.checkEventFlow();
    if (typeof google === 'undefined') {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.google_api_key}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const paramsSub = this.route.queryParams.subscribe((params) => {
      this.dataParams = Object.keys(params)
        .filter((key) => dataParams.includes(key))
        .reduce((result, key) => {
          result[key] =
            params[key] === 'true'
              ? true
              : params[key] === 'false'
              ? false
              : params[key];
          return result;
        }, {});
    });
    this.subscription.add(paramsSub);
  }

  getData() {
    const stepsDatasub = this.dataService
      .getStepsData(this.dataService.id)
      .subscribe((data) => {
        //this.ownershipRequiresVerification = data?.appointmentRequest?.isVerifyHomeOwnership && data?.appointmentRequest?.externalAgentId?.length > 0;
        this.ownershipRequiresVerification =
          data?.appointmentRequest?.isVerifyHomeOwnership; // to be adjusted with externalAgentId and isVerifyHomeOwnership
        if (this.ownershipRequiresVerification) this.addOwnershipControl();
        this.prepopulateStepData(data);
        if (
          (this.checkPropertyType(data) || this.checkWindowCount(data)) &&
          !this.isEvent
        ) {
          this.nextStep = PAGES.PROJECT_DETAILS;
        }
      });
    this.subscription.add(stepsDatasub);
  }

  ngAfterViewInit(): void {
    this.inputElement = document.getElementById(
      'streetAddress1'
    ) as HTMLInputElement;
    if (this.inputElement) {
      // Now you can safely interact with the input element
      this.loadGoogleMaps();
    } else {
      console.error('Input element not found');
      this.interval = setInterval(() => {
        this.inputElement = document.getElementById(
          'streetAddress1'
        ) as HTMLInputElement;
        this.counter += 1;
        if (this.inputElement || this.counter === 10) {
          clearInterval(this.interval);
          if (this.inputElement) {
            this.loadGoogleMaps();
          }
        }
      }, 1000);
    }
  }

  checkEventFlow() {
    const eventSub = this.navigationService
      .getEventData()
      .subscribe((event: EventData) => {
        this.isEvent = !!event?.isEvent;
      });
    this.subscription.add(eventSub);
  }

  checkPropertyType(data: FormData): boolean {
    const property = data?.appointmentRequest?.property;

    if (property?.propertyType) {
      const other = this.otherPropertyType === property?.propertyType;
      const nonPrimary = this.nonPrimaryResidenceReasons.includes(
        property?.propertyType
      );

      if (other) this.insideSalesReasonEnum = 864630013;
      if (nonPrimary) this.insideSalesReasonEnum = 864630017;

      if (other || nonPrimary) {
        return true;
      }
    }
    return false;
  }

  checkWindowCount(data: FormData): boolean {
    if (data?.appointmentRequest?.project?.windowCount < 2) {
      this.insideSalesReasonEnum = 864630002;
      return true;
    }
    return false;
  }

  loadGoogleMaps() {
    if (typeof google === 'undefined') {
      window['initMap'] = () => {
        this.initAutocomplete();
      };
    } else {
      this.initAutocomplete();
    }
  }

  initAutocomplete = () => {
    const input = document.getElementById('streetAddress1') as HTMLInputElement;
    if (input) {
      this.autocomplete = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: ['us'] },
      });
      this.autocomplete.addListener('place_changed', this.fillInAddress);
    } else {
      console.error('Input element not found');
    }
  };

  fillInAddress = () => {
    const place = this.autocomplete.getPlace();
    let streetAddress1 = '';
    let streetAddress2 = '';
    let zipcode = '';
    let city = '';
    let state = '';

    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
      // @ts-ignore remove once typings fixed
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number': {
          streetAddress1 = `${component.long_name} ${streetAddress1}`;
          break;
        }

        case 'route': {
          streetAddress1 += component.short_name;
          break;
        }

        case 'postal_code': {
          zipcode = `${component.long_name}`;
          break;
        }

        case 'subpremise': {
          streetAddress2 = `${component.long_name} ${streetAddress2}`;
          break;
        }

        case 'locality':
          city = component.long_name;
          break;

        case 'administrative_area_level_1': {
          state = component.short_name;
          break;
        }
      }
    }

    this.addressForm.patchValue({
      streetAddress1,
      streetAddress2,
      city,
      state,
      zipcode,
    });
    this.cdr.detectChanges();
  };

  buildForm() {
    this.addressForm = this.fb.group({
      streetAddress1: ['', [Validators.required, Validators.maxLength(50)]],
      streetAddress2: ['', Validators.maxLength(50)],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      state: ['', Validators.required],
      zipcode: ['', Validators.required],
    });
    this.subscribeToFormChanges();
  }

  addOwnershipControl() {
    if (!this.addressForm.contains('ownershipSelection')) {
      this.ownershipOptionsRadioGroup = this.ownershipOptions.map(
        (item: { id: string; value: string }) => {
          return {
            formControlName: 'ownershipSelection',
            label: item.value,
            id: item.id,
            value: item.id,
          };
        }
      );
      this.addressForm.addControl(
        'ownershipSelection',
        this.fb.control('', [Validators.required])
      );
    }
  }

  subscribeToFormChanges(): void {
    const formChangesSub = this.addressForm.valueChanges
      .pipe(
        tap(() => {
          this.checkFormValidity();
        }),
        debounceTime(1000)
      )
      .subscribe((value) => {
        if (this.ownershipRequiresVerification)
          this.callAddressValidationService(this.addressForm.value);
      });

    this.subscription.add(formChangesSub);
  }

  callAddressValidationService(value: any): void {
    this.dataService.getPropertyOwners(value).subscribe({
      next: (response) => {
        this.propertyOwners = (response?.propertyOwners || []).filter(
          (owner) => owner.firstName !== null || owner.lastName !== null
        );
        this.cdr.detectChanges();
      },
    });
  }

  checkFormValidity() {
    setTimeout(() => {
      this.nextButtonState.emitActivateNext(this.addressForm.valid);
    });
  }

  updateFormValues() {
    this.addressForm.patchValue({
      streetAddress1:
        this.previouslyFilledData?.property?.street1 ||
        this.dataParams?.addressStreet1 ||
        '',
      streetAddress2:
        this.previouslyFilledData?.property?.street2 ||
        this.dataParams?.addressStreet2 ||
        '',
      city:
        this.previouslyFilledData?.property?.city ||
        this.dataParams?.addressCity ||
        '',
      state:
        this.previouslyFilledData?.property?.state ||
        this.dataParams?.addressState ||
        '',
      zipcode:
        this.previouslyFilledData?.property?.zipcode ||
        this.dataParams?.addressZipcode ||
        '',
    });

    if (this.addressForm.contains('ownershipSelection')) {
      const sessionData = JSON.parse(this.previouslyFilledData?.sessionData);
      this.addressForm.patchValue({
        ownershipSelection: sessionData?.ownershipSelection,
      });
    }

    if (this.dataParams?.addressStreet1)
      this.addressForm.get('streetAddress1').markAsTouched();
    if (this.dataParams?.addressStreet2)
      this.addressForm.get('streetAddress2').markAsTouched();
    if (this.dataParams?.addressCity)
      this.addressForm.get('city').markAsTouched();
    if (this.dataParams?.addressState)
      this.addressForm.get('state').markAsTouched();
    if (this.dataParams?.addressZipcode)
      this.addressForm.get('zipcode').markAsTouched();
  }

  prepopulateStepData(data: FormData) {
    if (data?.appointmentRequest?.property) {
      this.previouslyFilledData = {
        property: data?.appointmentRequest?.property,
        sessionData: data?.sessionData,
      };
    }
    this.updateFormValues();
  }

  onClickNext = (): Observable<any> => {
    if (this.addressForm.valid) {
      return this.verifyHomeValue();
    }
    this.addressForm.markAllAsTouched();
    return of(false);
  };

  onClickBack = (): Observable<any> => {
    if (this.isEvent) return this.saveData(PAGES.HOMEOWNER_LIST);

    return this.addressForm.valid
      ? this.saveData(NAVIGATION_MAP[PAGES.ADDRESS].backButton.route)
      : of(true);
  };

  verifyHomeValue = (): Observable<any> => {
    const address = `${this.addressForm.value.streetAddress1} ${this.addressForm.value.streetAddress2} ${this.addressForm.value.city} ${this.addressForm.value.state} ${this.addressForm.value.zipcode}`;
    return this.dataService
      .propertyValidation({
        address: address,
        postalCode: this.addressForm.value.zipcode,
      })
      .pipe(
        switchMap((response) => {
          if (!this.isEvent) {
            if (
              !response.doesPropertyValueMeetThreshold &&
              !this.ownershipRequiresVerification
            ) {
              this.nextStep = PAGES.PROJECT_DETAILS;
            }
          }
          return this.dataService.serviceAvailability(this.addressForm.value.zipcode).pipe(
            tap((res) => {
              this.otherAddressProperties.marketSegmentGuid =
                res.marketSegmentId || '';
              this.otherAddressProperties.zipcodeGuid = res.zipcodeId || '';
              this.otherAddressProperties.zipcodeValid = res.isServiceAvailable;

              if (!res.isServiceAvailable) {
                this.nextStep = PAGES.DO_NOT_SERVICE_AREA;
              } else {
                if (
                  !response.doesPropertyValueMeetThreshold &&
                  !this.ownershipRequiresVerification &&
                  !this.isEvent
                ) {
                  this.nextStep = PAGES.PROJECT_DETAILS;
                }
              }
            }),
            switchMap(() => this.saveData())
          );
        }),
        catchError((error) => {
          console.error(error);
          return of(false);
        })
      );
  };

  saveData(nextPage?: string): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };

    stepsData.appointmentRequest.property.street1 =
      this.addressForm.value.streetAddress1;
    stepsData.appointmentRequest.property.street2 =
      this.addressForm.value.streetAddress2;
    stepsData.appointmentRequest.property.city = this.addressForm.value.city;
    stepsData.appointmentRequest.property.zipcode =
      this.addressForm.value.zipcode;
    stepsData.appointmentRequest.property.state = this.addressForm.value.state;

    stepsData.appointmentRequest.property.zipcodeValid =
      this.otherAddressProperties.zipcodeValid;
    stepsData.appointmentRequest.property.marketSegmentGuid =
      this.otherAddressProperties.marketSegmentGuid;
    stepsData.appointmentRequest.property.zipcodeGuid =
      this.otherAddressProperties.zipcodeGuid;

    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage || this.nextStep;
    if (this.addressForm.contains('ownershipSelection')) {
      sessionData.ownershipSelection =
        this.addressForm.value.ownershipSelection;
      this.checkHomeownershipSelection();
    }
    stepsData.sessionData = JSON.stringify(sessionData);

    if (this.nextStep === PAGES.PROJECT_DETAILS) {
      stepsData.appointmentRequest.insideSalesReasonEnum =
        this.insideSalesReasonEnum;
    }
    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        return nextPage || this.nextStep;
      })
    );
  }

  checkHomeownershipSelection() {
    if (
      this.addressForm.value.ownershipSelection ===
        this.ownershipIds.IM_NOT_SURE &&
      !this.isEvent
    ) {
      this.nextStep = PAGES.PROJECT_DETAILS;
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
